<template>
  <div class="step-panel">
    <!-- Step Header -->
    <div class="step-header">
      <h2>{{ t('Configuração do Período') }}</h2>
    </div>

    <!-- Helper Banner -->
    <div class="helper-banner">
      <i class="pi pi-info-circle"></i>
      <div class="helper-content">
        <strong>{{ t('Configuração do Período') }}</strong>
        <ul>
          <li>{{ t('Selecione a franquia e o colaborador para configurar o período de folha') }}</li>
          <li>{{ t('As datas serão calculadas automaticamente com base no último período do colaborador') }}</li>
          <li v-if="isEditMode">{{ t('Em modo de edição, as datas podem estar bloqueadas dependendo do status do período') }}</li>
        </ul>
      </div>
    </div>

    <!-- Warning Banner -->
    <div v-if="isEditMode" class="warning-banner">
      <i class="pi pi-exclamation-triangle"></i>
      <div class="warning-content">
        <strong>{{ t('Atenção') }}</strong>
        <p>{{ t('Você está em modo de edição. Algumas configurações podem não ser alteradas dependendo do status do período.') }}</p>
      </div>
    </div>

    <div class="p-fluid">
      <div class="p-field">
        <FranchiseSelect
          elem-id="franchise"
          v-model="periodFormData.franchise_id"
          :label="t('Franquia') + ' *'"
          :placeholder="t('Selecione a franquia')"
          :disabled="isEditMode"
          data-testid="payroll-period-franchise-select"
          @update:modelValue="onFranchiseChange"
        />
      </div>

      <!-- Employee selection (employee-centered architecture) -->
      <div class="p-field">
        <label for="user_id">{{ t('Colaborador') }} *</label>
        <select-list
          elem-id="user_id"
          v-model="periodFormData.user_id"
          :itens-select="availableEmployees"
          :placeholder="t('Selecione o colaborador')"
          :disabled="isEditMode || !periodFormData.franchise_id"
          data-testid="payroll-period-employee-select"
          @update:modelValue="onEmployeeChange"
        />
      </div>

      <!-- Loading state -->
      <div v-if="isLoadingDates" class="loading-state">
        <spinner style="width: 30px; height: 30px" />
        <span>{{ t('Calculando período...') }}</span>
      </div>

      <!-- Error state -->
      <div v-else-if="dateError" class="error-state">
        <i class="pi pi-exclamation-triangle"></i>
        <span>{{ dateError }}</span>
      </div>

      <!-- Period dates display (auto-calculated) -->
      <div v-else-if="!isLoadingDates && !dateError && periodFormData.start_date && periodFormData.end_date" class="period-display">
        <div class="period-header">
          <i class="pi pi-calendar"></i>
          <span>{{ t('Próximo período') }}</span>
        </div>
        <div class="period-dates">
          <div class="date-item">
            <span class="date-label">{{ t('Início do Período') }}</span>
            <span class="date-value">{{ formatDisplayDate(periodFormData.start_date) }}</span>
          </div>
          <div class="date-item">
            <span class="date-label">{{ t('Fim do Período') }}</span>
            <span class="date-value">{{ formatDisplayDate(periodFormData.end_date) }}</span>
          </div>
        </div>
        <div v-if="hasPreviousPeriod" class="period-info">
          <i class="pi pi-info-circle"></i>
          <span>{{ t('Continuação do período anterior que terminou em') }} {{ formatDisplayDate(previousEndDate) }}</span>
        </div>
        <div v-else class="period-info period-info-new">
          <i class="pi pi-plus-circle"></i>
          <span>{{ t('Primeiro período para este colaborador') }}</span>
        </div>
      </div>

      <!-- Instruction when no selection -->
      <div v-else-if="!isLoadingDates && !dateError && periodFormData.franchise_id && !periodFormData.user_id" class="instruction-state">
        <i class="pi pi-arrow-up"></i>
        <span>{{ t('Selecione o colaborador para calcular o período') }}</span>
      </div>

      <div v-else-if="!isLoadingDates && !dateError && !periodFormData.franchise_id" class="instruction-state">
        <i class="pi pi-arrow-up"></i>
        <span>{{ t('Selecione a franquia e o colaborador para calcular o período') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePayrollPeriods } from '@/composables/usePayrollPeriods';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { permissionsService } from '@/services/permissionsService';
import { log } from '@/utils/DebugLogger';
import type { Payroll_NextPeriodDatesResponseDados } from '@/contracts/Payroll_NextPeriodDatesResponse';

type Id = number | string | null;

interface PeriodFormData {
  franchise_id: Id;
  user_id: Id;
  start_date: string | null;
  end_date: string | null;
}

interface EmployeeOption {
  ID: Id;
  Name: string;
}

type NextPeriodDates = Payroll_NextPeriodDatesResponseDados;

interface WizardMachine {
  context: { value: { periodFormData?: Partial<PeriodFormData> } };
  isEditMode: { value: boolean };
  updateForm: (updates: Partial<PeriodFormData>) => void;
}

// =============================================================================
// INJECT MACHINE - Single Source of Truth
// =============================================================================

const machine = inject<WizardMachine>('wizardMachine')!;

const { t, d } = useI18n();
const { getNextPeriodDates } = usePayrollPeriods();
const { fromApiDateToDate } = useDateFormatter();

// =============================================================================
// REACTIVE DATA FROM MACHINE
// =============================================================================

// Get period form data from machine context (reactive)
// Employee-centered architecture: user_id instead of frequency
const periodFormData = computed((): PeriodFormData => ({
  franchise_id: null,
  user_id: null,
  start_date: null,
  end_date: null,
  ...machine.context.value.periodFormData,
}));

// Edit mode from machine
const isEditMode = computed((): boolean => machine.isEditMode.value);

// =============================================================================
// LOCAL STATE
// =============================================================================

const isLoadingDates = ref<boolean>(false);
const dateError = ref<string | null>(null);
const hasPreviousPeriod = ref<boolean>(false);
const previousEndDate = ref<string | null>(null);
const availableEmployees = ref<EmployeeOption[]>([]);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Format date for display using i18n
 */
function formatDisplayDate(dateString: string | null): string {
  if (!dateString) return '';
  const date = fromApiDateToDate(dateString);
  if (!date) return dateString;
  return d(date, 'short');
}

/**
 * Update machine with form data changes
 */
function updateMachine(updates: Partial<PeriodFormData>): void {
  log('Step1PeriodConfig', 'updateMachine', 'Updating form data', {
    updates,
    'current periodFormData': machine.context.value.periodFormData,
  });
  machine.updateForm(updates);
}

// =============================================================================
// EVENT HANDLERS
// =============================================================================

/**
 * Handle franchise change
 */
function onFranchiseChange(newValue: Id): void {
  log('Step1PeriodConfig', 'onFranchiseChange', 'Franchise changed', { newValue });
  updateMachine({ franchise_id: newValue, user_id: null }); // Clear employee when franchise changes
  availableEmployees.value = []; // Clear employees

  // Load employees for selected franchise
  if (newValue) {
    const employees = permissionsService.getFuncionariosSelectArrayByFranquiaID(Number(newValue));
    availableEmployees.value = employees.map((e: EmployeeOption) => ({
      ID: e.ID,
      Name: e.Name,
    }));
  }
}

/**
 * Handle employee change (employee-centered architecture)
 */
function onEmployeeChange(newValue: Id): void {
  log('Step1PeriodConfig', 'onEmployeeChange', 'Employee changed', { newValue });
  updateMachine({ user_id: newValue });
  fetchNextPeriodDates(periodFormData.value.franchise_id, newValue);
}

/**
 * Fetch and set next period dates
 * Employee-centered: uses user_id instead of frequency
 */
async function fetchNextPeriodDates(franchiseId: Id, userId: Id): Promise<void> {
  if (!franchiseId || !userId) {
    updateMachine({ start_date: null, end_date: null });
    hasPreviousPeriod.value = false;
    previousEndDate.value = null;
    return;
  }

  isLoadingDates.value = true;
  dateError.value = null;

  try {
    const dates: NextPeriodDates | undefined = await getNextPeriodDates(franchiseId, userId);

    if (dates) {
      updateMachine({ start_date: dates.start, end_date: dates.end });
      hasPreviousPeriod.value = dates.has_previous ?? false;
      previousEndDate.value = dates.previous_end_date ?? null;
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : t('Erro ao calcular período');
    dateError.value = message;
    updateMachine({ start_date: null, end_date: null });
    hasPreviousPeriod.value = false;
    previousEndDate.value = null;
  } finally {
    isLoadingDates.value = false;
  }
}

// =============================================================================
// WATCHERS
// =============================================================================

// Watch for franchise_id changes (including initial load in edit mode)
// This ensures availableEmployees is populated when modal opens with existing data
watch(
  () => periodFormData.value.franchise_id,
  (newFranchiseId, oldFranchiseId) => {
    if (newFranchiseId && newFranchiseId !== oldFranchiseId) {
      log('Step1PeriodConfig', 'watch:franchise_id', 'Loading employees for franchise', { newFranchiseId, oldFranchiseId });
      const employees = permissionsService.getFuncionariosSelectArrayByFranquiaID(Number(newFranchiseId));
      availableEmployees.value = employees.map((e: EmployeeOption) => ({
        ID: e.ID,
        Name: e.Name,
      }));
      log('Step1PeriodConfig', 'watch:franchise_id', 'Loaded employees', { count: availableEmployees.value.length });
    } else if (!newFranchiseId) {
      availableEmployees.value = [];
    }
  },
  { immediate: true }
);
</script>

<style scoped>
/* Step Header */
.step-header h2 {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

/* Helper Banner */
.helper-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 4px solid var(--blue-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--blue-700);
}

.helper-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.helper-content li {
  margin-bottom: 0.25rem;
  color: var(--blue-900);
  font-size: 0.9rem;
}

/* Warning Banner */
.warning-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--orange-50);
  border-left: 4px solid var(--orange-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.warning-banner i {
  color: var(--orange-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.warning-content strong {
  display: block;
  margin-bottom: 0.25rem;
  color: var(--orange-700);
}

.warning-content p {
  margin: 0;
  color: var(--orange-900);
  font-size: 0.9rem;
  line-height: 1.5;
}

.step-panel {
  width: 100%;
}

.p-field {
  margin-bottom: 1.5rem;
}

.p-field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
}

.loading-state,
.error-state,
.instruction-state {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 6px;
  margin-top: 0.5rem;
}

.loading-state {
  background: var(--surface-ground);
  color: var(--text-color-secondary);
}

.error-state {
  background: var(--red-50);
  color: var(--red-600);
}

.error-state .pi {
  color: var(--red-500);
}

.instruction-state {
  background: var(--surface-ground);
  color: var(--text-color-secondary);
  font-style: italic;
}

.instruction-state .pi {
  color: var(--primary-color);
}

.period-display {
  background: var(--surface-ground);
  border-radius: 6px;
  padding: 1rem;
  margin-top: 0.5rem;
}

.period-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-weight: 500;
  color: var(--primary-color);
}

.period-header .pi {
  font-size: 1.1rem;
}

.period-dates {
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
}

.date-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.date-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.date-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-color);
}

.period-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  padding-top: 0.75rem;
  border-top: 1px solid var(--surface-border);
}

.period-info .pi {
  color: var(--blue-500);
}

.period-info-new .pi {
  color: var(--green-500);
}
</style>
